import React, { useState } from "react";
import { useSelector } from 'react-redux';
import { Link, useNavigate } from "react-router-dom";
import user from '../../../../images/no_user_placeholder.png';
import { MessageTutorModal } from "./MessageTutorModal";
import { showErrorToast, showSuccessToast } from "../../../components/PluginsMenu/Toastr/Toastr";
import AverageStarRating from "../../../components/Dashboard/AverageStarRating";

export const TutorProfileHeader = ({ tutor }) => {
    const [showMessageModal, setShowMessageModal] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const navigate = useNavigate();
    
    // Get student info from Redux store
    const studentId = useSelector(state => state.auth.currentUser?.id);
    const studentEmail = useSelector(state => state.auth.currentUser?.email);
    const hasRatings = tutor.averageRating > 0 && tutor?.sampleReviews.length > 0;

    const handleMessageTutor = () => {
        if (!studentId) {
            alert("Please login to message tutors");
            return;
        }

        if (!tutor.hasBooked) {
            showErrorToast("You can message the tutor only after booking a session.");
            return;
        }
        setShowMessageModal(true);
    };

    const handleCloseModal = () => {
        setShowMessageModal(false);
    };

    const handleSendMessage = async (message) => {
        if (!studentId || !tutor?.id) return;
        
        setIsSending(true);
        try {
            showSuccessToast(`Message sent successfully to ${tutor.name}!`);
        } catch (error) {
            console.error("Error sending message:", error);
            showErrorToast("Failed to send message. Please try again.");
        } finally {
            setIsSending(false);
        }
    };

    const handleBookSession = () => {
        if (!studentId) {
            alert("Please login to book a session");
            return;
        }

        // Check if student email is null or empty
        if (tutor.isStudentEmailNull || !studentEmail) {
            // Redirect to personal details page to enter email
            showErrorToast("Please complete your profile by adding your email address before booking a session.");
            navigate('/personal-details');
            return;
        }

        // If email exists, navigate to booking page
        navigate(`/book-session?tutorId=${tutor.id}`, {
            state: {
                subject: tutor.language,
                subjectId: tutor.languageId
            }
        });
    };

    return (
        <>
            <div className="mb-3">
                <button className="btn p-0 d-flex align-items-center"  onClick={() => navigate(-1)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#000000" className="bi bi-arrow-left me-2" viewBox="0 0 16 16">
                        <path fillRule="evenodd" d="M15 8a.5.5 0 0 1-.5.5H2.707l4.147 4.146a.5.5 0 0 1-.708.708l-5-5a.5.5 0 0 1 0-.708l5-5a.5.5 0 1 1 .708.708L2.707 7.5H14.5A.5.5 0 0 1 15 8z"/>
                    </svg>
                </button>
            </div>

            <div className="card shadow-sm">
                <div className="card-body p-4">
                    <div className="row align-items-center justify-content-center justify-content-md-start">
                        <div className="col-auto text-center mb-3 mb-md-0 d-flex flex-column align-items-center">
                            <img
                                src={tutor.imagePath ? `${process.env.REACT_APP_IMAGE_PATH}${tutor.imagePath}` : user}
                                alt={tutor.name}
                                className="rounded-circle img-thumbnail"
                                style={{
                                    width: "140px",
                                    height: "140px",
                                    minWidth: "140px",
                                    minHeight: "140px",
                                    objectFit: "cover"
                                }}
                                onError={(e) => { e.target.src = user; }}
                            />
                             <div className="mb-2 mt-2">
                                <div className="d-flex align-items-center justify-content-center">
                                    <i className="fa fa-star text-warning me-2"></i>
                                    {hasRatings ? (
                                        <div className="d-flex align-items-center flex-column">
                                            <AverageStarRating level={tutor.averageRating} />
                                            <span className="fw-bold text-dark ms-2">
                                                {tutor.averageRating.toFixed(1)}
                                            </span>
                                            <small className="text-muted ms-2">
                                                ({tutor.sampleReviews?.length} review{tutor.sampleReviews?.length !== 1 ? 's' : ''})
                                            </small>
                                        </div>
                                         ) : (
                                        <span className="text-muted fst-italic">Not rated yet</span>
                                    )}
                                </div>
                            </div>                        
                        </div>

                        <div className="col-md-7 col-sm-9 col-12 text-lg-start text-md-start text-center">
                            <h3 className="mb-3">{tutor.name}</h3>
                            <div className="row">
                                <div className="col-md-6 col-12">
                                    <p><strong>Gender:</strong> {tutor.gender}</p>
                                    <p><strong>Based in:</strong> {tutor.location}</p>
                                    <p><strong>Also Speaks:</strong> {tutor.dialect}</p>
                                    <p><strong>Experience:</strong> {tutor.experienceLevel} years</p>
                                </div>
                                <div className="col-md-6 col-12">
                                    <p><strong>Age:</strong> {tutor.age}</p>
                                    <p><strong>Education:</strong> {tutor.education}</p>
                                    <p><strong>Teaches:</strong> {tutor.language}</p>
                                    <p><strong>Timezone:</strong> {tutor.timezone}</p>
                                </div>
                                <div className="col-12">
                                    <p><strong>About:</strong> {tutor.about}</p>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-3 col-sm-12 mt-3 mt-md-0 text-md-end">
                            <button
                                onClick={handleBookSession}
                                className="btn btn-primary w-100 mb-2"
                            >
                                <i className="fas fa-calendar-check me-2"></i>
                                Book a Session
                            </button>
                            
                            <button 
                                className="btn btn-outline-primary w-100 mb-2"
                                onClick={handleMessageTutor}
                                disabled={!studentId  || !tutor.hasBooked}
                            >
                                <i className="fas fa-envelope me-2"></i>
                                Message Tutor 
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Message Tutor Modal */}
            <MessageTutorModal
                show={showMessageModal}
                onClose={handleCloseModal}
                onSendMessage={handleSendMessage}
                tutor={tutor}
                studentId={studentId}
                isSending={isSending}
            />
        </>
    );
};